import { useState, useEffect } from 'react'
import { Smartphone, RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react'
import { apiService } from '../../services/api'

interface WalletPassStats {
  userId: string
  deviceCount: number
  lastUpdate: string | null
  platforms: Array<'apple' | 'google'>
  passes: Array<{
    serialNumber: string
    platform: 'apple' | 'google'
    deviceCount: number
    lastUpdated: string
    createdAt: string
  }>
}

interface WalletPassUpdateButtonProps {
  userId: string
  userName: string
}

export default function WalletPassUpdateButton({ userId, userName }: WalletPassUpdateButtonProps) {
  const [stats, setStats] = useState<WalletPassStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [updateResult, setUpdateResult] = useState<{
    success: boolean
    message: string
  } | null>(null)

  useEffect(() => {
    loadStats()
  }, [userId])

  const loadStats = async () => {
    setLoading(true)
    try {
      const response = await apiService.getWalletPassStats(userId)
      if (response.data && !response.error) {
        setStats(response.data as WalletPassStats)
      }
    } catch {
      // endpoint not yet available
    }
    setLoading(false)
  }

  const handleTriggerUpdate = async () => {
    if (!confirm(`Trigger wallet pass update for ${userName}?\n\nThis will send push notifications to all registered devices.`)) {
      return
    }

    setUpdating(true)
    setUpdateResult(null)

    const response = await apiService.triggerWalletPassUpdate({
      userId,
      updateReason: 'Manual update triggered by admin'
    })

    if (response.error) {
      setUpdateResult({
        success: false,
        message: response.error.message
      })
    } else {
      const data = response.data as any
      setUpdateResult({
        success: true,
        message: `Update sent to ${data.summary?.totalDevicesNotified || 0} device(s)`
      })
      // Reload stats after update
      setTimeout(() => loadStats(), 1000)
    }

    setUpdating(false)

    // Clear result after 5 seconds
    setTimeout(() => setUpdateResult(null), 5000)
  }

  if (!stats) {
    return (
      <button onClick={loadStats} disabled={loading} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
        {loading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Smartphone className="h-3 w-3" />}
        {loading ? 'Loading...' : 'Check passes'}
      </button>
    )
  }

  if (stats.deviceCount === 0) {
    return (
      <div className="text-sm text-gray-400">
        No wallet passes
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          onClick={handleTriggerUpdate}
          disabled={updating}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {updating ? (
            <>
              <RefreshCw className="h-3 w-3 animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Smartphone className="h-3 w-3" />
              Push Update
            </>
          )}
        </button>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {showDetails ? 'Hide' : 'Show'} Details
        </button>
      </div>

      {/* Update Result */}
      {updateResult && (
        <div className={`text-sm flex items-center gap-1 ${
          updateResult.success ? 'text-green-600' : 'text-red-600'
        }`}>
          {updateResult.success ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {updateResult.message}
        </div>
      )}

      {/* Stats Summary */}
      <div className="text-xs text-gray-600 flex items-center gap-3">
        <span className="flex items-center gap-1">
          <Smartphone className="h-3 w-3" />
          {stats.deviceCount} device{stats.deviceCount !== 1 ? 's' : ''}
        </span>
        {stats.lastUpdate && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(stats.lastUpdate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        )}
        <span className="flex items-center gap-1">
          {(stats.platforms || []).map(p => (
            <span key={p} className="px-1 py-0.5 bg-gray-100 rounded text-xs">
              {p === 'apple' ? '🍎' : '🤖'} {p}
            </span>
          ))}
        </span>
      </div>

      {/* Detailed Stats */}
      {showDetails && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
          <div className="font-medium text-gray-700">Pass Details:</div>
          {(stats.passes || []).map((pass, idx) => (
            <div key={idx} className="pl-2 text-gray-600">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{pass.serialNumber.substring(0, 16)}...</span>
                <span className="px-1 py-0.5 bg-gray-200 rounded">
                  {pass.platform === 'apple' ? '🍎 Apple' : '🤖 Google'}
                </span>
                <span>{pass.deviceCount} device{pass.deviceCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="text-gray-500 pl-2">
                Last updated: {new Date(pass.lastUpdated).toLocaleString('en-GB')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
